import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { SourceField } from 'app/features/admin/modules/modules.model';
import { opensInPanel } from '../reveal-menu/reveal.util';
import { createSlug } from 'app/features/admin/modules/features/form-builder/form-generate.util.mjs';

/** Keys under which a source field nests its children (mirrors FormBuilderService). */
const CHILD_KEYS = [
  'repeatFields',
  'groupFields',
  'accordionFields',
  'tabsFields',
  'pagesFields',
  'wizardFields',
  'sectionFields',
] as const satisfies readonly (keyof SourceField)[];

type ChildKey = (typeof CHILD_KEYS)[number];

/** One step down the source tree: the field and the list its next step sits in. */
export interface SourceLink {
  parent: SourceField;
  container: ChildKey;
}

/**
 * The chain of containers above `target`, outermost first, or undefined when `target` is not in
 * `tree`. Matches by identity: the form builder edits the very objects of its model.
 */
export function findSourceLinks(tree: SourceField[] | undefined, target: SourceField): SourceLink[] | undefined {
  for (const field of tree ?? []) {
    if (field === target) {
      return [];
    }
    for (const container of CHILD_KEYS) {
      const children = field[container];
      if (!Array.isArray(children)) {
        continue;
      }
      const below = findSourceLinks(children, target);
      if (below) {
        return [{ parent: field, container }, ...below];
      }
    }
  }
  return undefined;
}

/** The container directly above `target`: a section, a route's stops, ... */
export function directParent(links: SourceLink[] | undefined): SourceLink | undefined {
  return links?.[links.length - 1];
}

/** True when `target` sits in a repeat or a route's stops (any depth). Unknown placement counts as outside. */
export function isInsideRepeat(links: SourceLink[] | undefined): boolean {
  return !!links?.some(link => link.container === 'repeatFields');
}

/** The key a field gets in the generated form: its own, or the slug of its label. */
export function sourceKey(field: SourceField): string | undefined {
  return field.key || createSlug(field.label) || undefined;
}

export function isRequired(field: SourceField): boolean {
  return (field.required as unknown) === true || (field.required as unknown) === 'true';
}

export interface KeyOption {
  value: string;
  label: string;
}

/** Picker options for a list of sibling fields. Required ones are tagged: they should not be hidden. */
export function siblingOptions(children: SourceField[] | undefined): KeyOption[] {
  return (children ?? []).flatMap(child => {
    const key = sourceKey(child);
    return key ? [{ value: key, label: `${child.label || key}${isRequired(child) ? ' (verplicht)' : ''}` }] : [];
  });
}

/**
 * Warning for a reveal list that names a required field: a hidden field leaves validation, so a
 * required one stops being required until the user picks it (spec §5.4).
 */
export function revealRequiredWarning(
  children: SourceField[] | undefined,
  selected: readonly string[] | undefined
): string | undefined {
  const required = (children ?? [])
    .filter(child => isRequired(child) && selected?.includes(sourceKey(child) ?? ''))
    .map(child => child.label || sourceKey(child));
  return required.length
    ? `${required.join(', ')} ${required.length > 1 ? 'zijn' : 'is'} verplicht. Een verborgen veld telt niet mee in de validatie, dus de plicht vervalt tot het veld gekozen is. Kies alleen optionele velden.`
    : undefined;
}

/** True when `child` is listed in one of its parent's reveal lists (section slots or route stops). */
export function isListedInReveal(parent: SourceField | undefined, child: SourceField): boolean {
  const key = sourceKey(child);
  return (
    !!parent &&
    !!key &&
    [
      parent.departureRevealFields,
      parent.destinationRevealFields,
      parent.footerRevealFields,
      parent.stopRevealFields,
    ].some(list => list?.includes(key))
  );
}

/**
 * The other fields in the same reveal list as `child`: the hosts it can ride with (`revealWith`).
 * Section children are matched per slot list; a route's stops by `stopRevealFields`. Only fields
 * that open in a panel, and no field that rides itself (runtime ignores both as hosts).
 */
export function revealHostCandidates(parent: SourceField | undefined, child: SourceField): SourceField[] {
  const key = sourceKey(child);
  const lists = [
    { list: parent?.departureRevealFields, children: parent?.sectionFields },
    { list: parent?.destinationRevealFields, children: parent?.sectionFields },
    { list: parent?.stopRevealFields, children: parent?.repeatFields },
  ];
  const home = lists.find(({ list }) => key && list?.includes(key));
  return (home?.children ?? []).filter(
    other =>
      other !== child &&
      !other.revealWith &&
      opensInPanel({ type: other.type } as FormlyFieldConfig) &&
      !!sourceKey(other) &&
      home?.list?.includes(sourceKey(other)!)
  );
}

/**
 * Drops the position props a field keeps after it was moved out of the place that reads them:
 * `routeSlot` outside a route section, `stopSlot` outside a route's stops, `revealIcon` when no
 * reveal list names the field (`revealWith` with it). Mutates `child`.
 */
export function stripStaleSlotProps(child: SourceField, parent: SourceField | undefined): void {
  if (parent?.type !== 'section' || parent.sectionLayout !== 'route') delete child.routeSlot;
  if (parent?.type !== 'route') delete child.stopSlot;
  if (!isListedInReveal(parent, child)) {
    delete child.revealIcon;
    delete child.revealWith;
  }
}
